import { useState } from 'react';
import { Check, Lock, ShieldCheck } from 'lucide-react';
import { apiClient } from '@/api';
import { ACCESS_REQUEST_API_URLS } from '@/api/constants/api.url';
import { cn } from '@/shared/utils/cn';
import { permissionLabel, type AccessKey } from '@/shared/permissions';

/**
 * Shown when a member is blocked by their ROLE, not by the plan.
 *
 * The upgrade pitch is wrong here: a managed member's access is decided by the
 * workspace admin in Settings → Role Matrix, and no amount of billing changes
 * it. So this panel just states the block and offers to ping the admin.
 */
export function AccessDeniedContent({
    feature,
    variant = 'modal',
    onClose,
}: {
    feature?: AccessKey;
    variant?: 'modal' | 'page';
    onClose?: () => void;
}) {
    const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const label = feature ? permissionLabel(feature) : null;
    const what = label ? `“${label}”` : 'This section';

    const requestAccess = async () => {
        if (!feature || state === 'sending' || state === 'sent') return;
        setState('sending');
        try {
            await apiClient.post(ACCESS_REQUEST_API_URLS.create, { feature });
            setState('sent');
        } catch {
            setState('error');
        }
    };

    return (
        <div
            className={cn(
                'w-full bg-white p-8 md:p-10',
                variant === 'modal'
                    ? 'max-w-lg rounded-[24px] border border-ink-100 shadow-elevated'
                    : 'rounded-2xl border border-ink-100 shadow-soft',
            )}
        >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ink-50 ring-1 ring-ink-100">
                <Lock className="h-6 w-6 text-ink-500" />
            </div>

            <h1 className="mt-6 font-display text-2xl font-bold leading-tight text-brand-navy">
                You don’t have access
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-ink-500">
                {what} isn’t part of your access. Your workspace admin can enable it for you
                from Settings → Role Matrix.
            </p>

            <div className="mt-5 flex items-start gap-2.5 rounded-2xl bg-ink-50 p-4 text-sm leading-relaxed text-ink-700">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-sky-500" />
                <span>
                    Access is set per role, so anyone else with your role is affected by the
                    same setting.
                </span>
            </div>

            <div className="mt-6 flex flex-col gap-2">
                {state === 'sent' ? (
                    <div className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-700">
                        <Check className="h-4 w-4" /> Request sent to your workspace admin
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => void requestAccess()}
                        disabled={!feature || state === 'sending'}
                        className={cn(
                            'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white',
                            'bg-brand-navy transition-colors hover:bg-brand-navy-600 disabled:opacity-50',
                        )}
                    >
                        {state === 'sending' ? 'Sending…' : 'Request access'}
                    </button>
                )}
                {state === 'error' && (
                    <p className="text-center text-xs text-red-600">
                        Couldn’t send the request. Please try again.
                    </p>
                )}
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl px-5 py-2.5 text-sm font-medium text-ink-500 transition-colors hover:bg-ink-50"
                    >
                        Close
                    </button>
                )}
            </div>
        </div>
    );
}
